import React from 'react';
import styled from 'styled-components';

interface PaginationProps {
  currentPage: number;
  totalPages: number;
  onPageChange: (page: number) => void;
}

const PaginationContainer = styled.div`
  display: flex;
  justify-content: center;
  margin: 2rem 0;
  align-items: center;
  
  @media (max-width: 768px) {
    margin: 1.5rem 0;
    flex-wrap: wrap;
    padding: 0 10px;
  }
`;

const PageButton = styled.button<{ isActive?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 2.2rem;
  height: 2.2rem;
  padding: 0 0.5rem;
  margin: 0 0.25rem;
  border: 1px solid ${props => props.isActive 
    ? props.theme.colors.primary 
    : props.theme.colors.border};
  border-radius: ${props => props.isActive 
    ? props.theme.borderRadius.sm 
    : props.theme.borderRadius.sm};
  background-color: ${props => props.isActive 
    ? props.theme.colors.primary 
    : 'white'};
  color: ${props => props.isActive ? 'white' : props.theme.colors.text};
  font-size: ${props => props.theme.fontSizes.sm};
  cursor: pointer;
  transition: all 0.2s ease;
  box-shadow: ${props => props.isActive 
    ? '0 2px 4px rgba(0, 0, 0, 0.15)' 
    : '0 1px 2px rgba(0, 0, 0, 0.05)'};

  &:hover {
    background-color: ${props => props.isActive
      ? props.theme.colors.primary
      : '#f0f5ff'};
    border-color: ${props => props.isActive
      ? props.theme.colors.primary
      : props.theme.colors.primary};
    transform: translateY(-1px);
  }

  &:active {
    transform: translateY(0);
  }

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
    background-color: ${props => props.theme.colors.light};
    border-color: ${props => props.theme.colors.border};
    box-shadow: none;
    transform: none;
  }
  
  @media (max-width: 768px) {
    min-width: 1.8rem;
    height: 1.8rem;
    padding: 0 0.3rem;
    margin: 0 0.15rem;
    font-size: 0.8rem;
  }
`;

const ArrowButton = styled(PageButton)`
  font-weight: bold;
  background: ${props => props.isActive ? props.theme.colors.primary : 'linear-gradient(to bottom, #ffffff, #f5f5f5)'};
  
  @media (max-width: 480px) {
    &.first-last {
      display: none;
    }
  }
`;

const Ellipsis = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 2rem;
  height: 2rem;
  color: ${props => props.theme.colors.text};
  font-weight: bold;
  
  @media (max-width: 768px) {
    width: 1.5rem;
    height: 1.8rem;
  }
  
  @media (max-width: 480px) {
    display: none;
  }
`;

const PaginationGroup = styled.div`
  display: flex;
  align-items: center;
  background-color: white;
  border-radius: 4px;
  padding: 3px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  
  @media (max-width: 768px) {
    box-shadow: 0 1px 4px rgba(0, 0, 0, 0.08);
  }
`;

const Pagination: React.FC<PaginationProps> = ({ 
  currentPage, 
  totalPages, 
  onPageChange 
}) => {
  // 페이지 범위 설정 (모바일 대응, 화면 크기에 따라 보여줄 페이지 수 조정)
  const getPageRange = () => {
    const range: number[] = [];
    // 모바일에서는 최대 3페이지, 데스크탑에서는 최대 5페이지 표시
    const isMobile = window.innerWidth <= 768;
    const maxVisiblePages = isMobile ? 3 : 5;
    
    // 시작 페이지와 종료 페이지 계산
    let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
    let endPage = startPage + maxVisiblePages - 1;
    
    // 종료 페이지가 전체 페이지 수를 초과하는 경우 조정
    if (endPage > totalPages) {
      endPage = totalPages;
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }
    
    // 페이지 범위 생성
    for (let i = startPage; i <= endPage; i++) {
      range.push(i);
    }
    
    return range;
  };

  // 페이지가 1개여도 페이지네이션 표시
  const pageRange = getPageRange();
  
  if (totalPages <= 1) return null;
  
  return (
    <PaginationContainer>
      <PaginationGroup>
        <ArrowButton 
          className="first-last"
          onClick={() => onPageChange(1)} 
          disabled={currentPage === 1}
        >
          &laquo;
        </ArrowButton>
        
        <ArrowButton 
          onClick={() => onPageChange(currentPage - 1)} 
          disabled={currentPage === 1}
        >
          &lsaquo;
        </ArrowButton>
        
        {pageRange[0] > 1 && (
          <>
            <PageButton onClick={() => onPageChange(1)}>1</PageButton>
            {pageRange[0] > 2 && <Ellipsis>···</Ellipsis>}
          </>
        )}
        
        {pageRange.map(page => (
          <PageButton 
            key={page} 
            isActive={currentPage === page}
            onClick={() => onPageChange(page)}
          >
            {page}
          </PageButton>
        ))}
        
        {pageRange[pageRange.length - 1] < totalPages && (
          <>
            {pageRange[pageRange.length - 1] < totalPages - 1 && <Ellipsis>···</Ellipsis>}
            <PageButton onClick={() => onPageChange(totalPages)}>
              {totalPages}
            </PageButton>
          </>
        )}
        
        <ArrowButton 
          onClick={() => onPageChange(currentPage + 1)} 
          disabled={currentPage === totalPages}
        >
          &rsaquo;
        </ArrowButton>
        
        <ArrowButton 
          className="first-last"
          onClick={() => onPageChange(totalPages)} 
          disabled={currentPage === totalPages}
        >
          &raquo;
        </ArrowButton>
      </PaginationGroup>
    </PaginationContainer>
  );
};

export default Pagination; 